import type { ModalGroup, ModalItem } from '@core/components'
import type {
  BaseEntity,
  FaultInfo,
  GridBuildFormData,
  MinimalCurvatureParams,
  MovingAverageParams,
  RbfKernel,
  SequentialConvergenceParams,
} from '@core/core/types'
import type { Nullable, ProjectId } from 'common/types'

export type MapBuildingResult = {
  formData: GridBuildFormData
  updateSourcesData: OnUpdateSourcesData
  updateGeometryData: OnUpdateGeometryData
  updateTargetData: OnUpdateTargetData
  updateKrigingData: OnUpdateKrigingData
  updateKernelData: OnUpdateKernelData
  updateFaultsData: OnUpdateFaultsData
  updateSequentialConvergenceData: OnUpdateSequentialConvergenceData
  updateMinimalCurvatureData: OnUpdateMinimalCurvatureData
  updateMovingAverageData: OnUpdateMovingAverageData
}

export type OnUpdateFaultsData = (faults: Pick<FaultInfo, 'id' | 'domain'>[]) => void

export type OnUpdateKernelData = (
  callback: (prev: GridBuildFormData['kernel']) => GridBuildFormData['kernel'] | undefined,
) => void

export type OnUpdateSequentialConvergenceData = (
  callback: (
    prev: Nullable<SequentialConvergenceParams>,
  ) => Nullable<SequentialConvergenceParams> | undefined,
) => void

export type OnUpdateMinimalCurvatureData = (
  callback: (
    prev: Nullable<MinimalCurvatureParams>,
  ) => Nullable<MinimalCurvatureParams> | undefined,
) => void

export type OnUpdateMovingAverageData = (
  callback: (prev: Nullable<MovingAverageParams>) => Nullable<MovingAverageParams> | undefined,
) => void

export type OnUpdateSourcesData = (
  callback: (
    prev: GridBuildFormData['buildInfo']['sources'],
  ) => GridBuildFormData['buildInfo']['sources'] | undefined,
) => void

export type OnUpdateGeometryData = (
  callback: (
    prev: GridBuildFormData['buildInfo']['geometry'],
  ) => GridBuildFormData['buildInfo']['geometry'] | undefined,
) => void

export type OnUpdateTargetData = (
  callback: (
    prev: GridBuildFormData['buildInfo']['name'],
  ) => GridBuildFormData['buildInfo']['name'],
) => void

export type OnUpdateKrigingData = (
  callback: (prev: GridBuildFormData['kriging']) => GridBuildFormData['kriging'] | undefined,
) => void

export type MethodItemsType = {
  label: string
  key: string
}
export type ChoiceItemsType = {
  name: string
}

export type RBFSelectItem = {
  label: string
  key: RbfKernel
}

export const methodItems: MethodItemsType[] = [
  {
    label: 'Кригинг',
    key: 'kriging',
  },
  {
    label: 'Радиальных базисных функций',
    key: 'RBF',
  },
  {
    label: 'Последовательных схождений',
    key: 'sequentialConvergence',
  },
  {
    label: 'Минимальной кривизны',
    key: 'minimumCurvature',
  },
  {
    label: 'Скользящего среднего',
    key: 'movingAverage',
  },
]

export const choiceItems: ChoiceItemsType[] = [
  {
    name: 'Авто',
  },
  {
    name: 'Ручная',
  },
]

export const rbfSelectItems: RBFSelectItem[] = [
  {
    label: 'Сплайн плоской пластины',
    key: 'FlatSpline',
  },
  {
    label: 'Линейная',
    key: 'Bilinear',
  },
]

export type AddedSelectItemType = {
  id: string
  value: Nullable<BaseEntity>
  color?: string
}
export type UpdateSelectItemListFunc<T = AddedSelectItemType[]> = (
  args: T | ((prevState: T) => T),
) => void

export type ModalBuildingRightMenuProps = {
  activeStep: MapBuilderItemType
  setMethodValue: (arg: MethodItemsType) => void
  methodValue: MethodItemsType
  setChoiceValue: (arg: ChoiceItemsType) => void
  choiceValue: ChoiceItemsType
  faultsFolderValue: Nullable<BaseEntity>
  onFaultsFolderUpdate: (value: Nullable<BaseEntity>) => void
  formData: GridBuildFormData
  updateSourcesData: OnUpdateSourcesData
  updateGeometryData: OnUpdateGeometryData
  updateTargetData: OnUpdateTargetData
  updateKrigingData: OnUpdateKrigingData
  addedSelectItemList: AddedSelectItemType[]
  updateSelectItemList: UpdateSelectItemListFunc
  updateKernelData: OnUpdateKernelData
  updateFaultsData: OnUpdateFaultsData
  updateSequentialConvergenceData: OnUpdateSequentialConvergenceData
  updateMinimalCurvatureData: OnUpdateMinimalCurvatureData
  updateMovingAverageData: OnUpdateMovingAverageData
}

export type MapBuildingProps = {
  closeModal: VoidFunction
  onClose?: VoidFunction
  projectId: ProjectId
}

type MapBuilderGroupType = 'input' | 'settings' | 'results'
export type MapBuilderItemType =
  | 'object'
  | 'geometry'
  | 'algorithms'
  | 'correction'
  | 'additionalObjects'
  | 'map'

export type MapBuilderGroup = ModalGroup<MapBuilderGroupType>

export type MapBuilderItem = ModalItem<MapBuilderItemType, MapBuilderGroupType>
